import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, functions } from '../../../shared/lib/supabase';
import { logger } from '../../../shared/lib/logger';

interface CreateSubscriptionData {
  product_id: string;
  billing_type: 'CREDIT_CARD' | 'BOLETO' | 'PIX' | 'UNDEFINED' | 'FREE';
  // Campos opcionais - não são mais necessários com Payment Links
  credit_card_token?: string;
  credit_card_data?: {
    number: string;
    holderName: string;
    expiryMonth: string;
    expiryYear: string;
    ccv: string;
  };
  credit_card_holder_info?: {
    name: string;
    email: string;
    cpfCnpj: string; // CPF/CNPJ sem formatação
    phone: string; // Telefone sem formatação
    postalCode: string; // CEP sem formatação
    address: string;
    addressNumber: string;
    complement?: string;
    province: string; // Bairro
    city: string; // Código IBGE (numérico)
  };
  subscription?: {
    cycle: 'MONTHLY' | 'WEEKLY' | 'QUARTERLY' | 'YEARLY';
    value: number;
  };
}

export function useCreateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSubscriptionData) => {
      try {
        const result = await functions.invoke('create-subscription', { body: data });
        return result;
      } catch (err: any) {
        logger.error('Error invoking create-subscription', err);
        // Re-throw so mutation onError can handle it and UI can show a friendly message
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-products'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
    onError: (error: any) => {
      logger.error('Erro ao criar assinatura', error);
    },
  });
}

export function useUserSubscriptions() {
  return useQuery({
    queryKey: ['subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_subscriptions')
        .select('*, product:products(*)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      const data = await functions.invoke('cancel-subscription', { body: { subscription_id: subscriptionId } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}
